import React, { useCallback, useEffect, useMemo, useState } from "react";
import _ from "lodash";
import { ICustomizableTableColumn } from "constants/table-types";
import { TableCellProps } from "react-virtualized";
import { arrayMove, SortableHandle } from "react-sortable-hoc";
import { IAddress } from "components/contacts/contacts-types";
import makeStyles from "@material-ui/core/styles/makeStyles";
import { format } from "lib/globalize-util";
import Checkbox from "@material-ui/core/Checkbox";
import Tooltip from "@material-ui/core/Tooltip";
import PartialIcon from "@material-ui/icons/IndeterminateCheckBox";
import { SortableTableWithSearch } from "components/orders/order/sortable-table-with-search";
import Dialog from "@material-ui/core/Dialog";
import { DialogFooter, DialogHeader } from "components/dialog/media-dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DragIcon from "@material-ui/icons/DragHandle";
import { DATATEST } from "constants/integration";
import theme from "styles/theme";
import { ILineItem } from "constants/order-types";
import ErrorIcon from "@material-ui/icons/Error";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import classnames from "classnames";

interface ISelectedShippingLocation {
    location: string;
}

interface IRow extends IAddress {
    checked: boolean;
    hasQuantities: boolean;
}

interface IProps {
    addresses: IAddress[];
    selectedShippingLocations: ISelectedShippingLocation[] | null;
    lineItems: ILineItem[];
    onSave: (selectedShippingLocations: ISelectedShippingLocation[]) => void;
    onCancel: () => void;
    open: boolean;
}

const DragHandle = SortableHandle(() => (
    <DragIcon
        data-test={DATATEST.WORKING_ORDER.DOORS_SELECT.DRAG_HANDLE}
        style={{ zIndex: 99, cursor: "pointer", fontSize: 24 }}
    />
));

export const EditShippingLocationsModal = (props: IProps) => {
    const { addresses, selectedShippingLocations, onSave, onCancel, open, lineItems } = props;

    const classes = useStyles({});

    const shippingLocationsWithQuantitiesSet = useMemo(
        () => getShippingLocationsWithQuantitiesSet(lineItems),
        [lineItems]
    );

    const [doors, setDoors] = useState<IRow[]>([]);
    useEffect(() => {
        setDoors(
            _.isEmpty(addresses)
                ? []
                : getDoorsToDisplay(addresses, selectedShippingLocations, shippingLocationsWithQuantitiesSet)
        );
    }, [addresses, selectedShippingLocations, shippingLocationsWithQuantitiesSet]);

    const [isWarningOpen, setIsWarningOpen] = useState(false);
    const handleShowWarningModal = useCallback(() => setIsWarningOpen(true), []);
    const handleHideWarningModal = useCallback(() => setIsWarningOpen(false), []);

    const selectedShippingLocationsCount = useMemo(() => _.filter(doors, "checked").length, [doors]);
    const isAllSelected = useMemo(
        () => selectedShippingLocationsCount === doors.length,
        [selectedShippingLocationsCount, doors]
    );
    const handleToggleSelectAll = useCallback(() => {
        setDoors((doors) => _.map(doors, (door) => ({ ...door, checked: !isAllSelected })));
    }, [isAllSelected]);

    const handleToggleSelectDoor = useCallback(
        (rowData: IRow) => () => {
            rowData.checked = !rowData.checked; // mutate clicked row data
            setDoors((doors) => _.orderBy(doors, "checked", "desc"));
        },
        []
    );

    const columnsConfig = useMemo<ICustomizableTableColumn[]>(
        () => [
            {
                menuLabel: "dragHandle",
                dataKey: "dragHandle",
                key: "dragHandle",
                width: 50,
                flexGrow: 0,
                flexShrink: 0,
            },
            {
                label: (
                    <span data-test={DATATEST.WORKING_ORDER.DOORS_SELECT.SELECT_ALL}>
                        <Checkbox
                            checked={selectedShippingLocationsCount > 0}
                            className={classes.checkbox}
                            onChange={handleToggleSelectAll}
                            checkedIcon={isAllSelected ? undefined : <PartialIcon />}
                        />
                    </span>
                ),
                menuLabel: "checkbox",
                dataKey: "checkbox",
                key: "checkbox",
                width: 50,
                flexGrow: 0,
                flexShrink: 0,
            },
            {
                label: format("Description"),
                dataKey: "display_name",
                key: "display_name",
                width: 200,
                flexGrow: 1,
                flexShrink: 0,
            },
            {
                label: (
                    <span
                        className={classes.selectedLocationsText}
                        data-test={DATATEST.WORKING_ORDER.DOORS_SELECT.SELECTED_LOCATIONS}
                    >
                        {format("|n| locations selected", { n: selectedShippingLocationsCount })}
                    </span>
                ),
                headerClassName: classes.selectedLocations,
                dataKey: "selected_count",
                key: "selected_count",
                width: 30,
                flexGrow: 0,
                flexShrink: 0,
            },
        ],
        [selectedShippingLocationsCount, handleToggleSelectAll, isAllSelected, classes]
    );

    const handleUpdateRowIndex = useCallback((oldIndex: number, newIndex: number) => {
        setDoors((doors) => arrayMove(doors, oldIndex, newIndex));
    }, []);

    const cellRenderer = useCallback(
        (cellProps: TableCellProps & { rowData: IRow }) => {
            const { dataKey, rowData, rowIndex } = cellProps;
            switch (dataKey) {
                case "dragHandle":
                    return <DragHandle />;
                case "checkbox":
                    return (
                        <Checkbox
                            checked={rowData.checked}
                            className={classes.checkbox}
                            onClick={handleToggleSelectDoor(rowData)}
                        />
                    );
                case "display_name":
                    const value = _.get(rowData, "display_name") || _.get(rowData, "line_1", "--");
                    return (
                        <Tooltip title={<span className={classes.tooltip}>{value}</span>} placement="top" arrow={true}>
                            <div
                                data-test={DATATEST.WORKING_ORDER.DOORS_SELECT.SELECTION_NAME}
                                className={classes.ellipsis}
                            >
                                {value}
                            </div>
                        </Tooltip>
                    );
                case "selected_count":
                    return rowData.hasQuantities && !rowData.checked ? (
                        <Tooltip
                            title={
                                <span
                                    data-test={DATATEST.WORKING_ORDER.DOORS_SELECT.SELECTION_WARNING_TOOLTIP}
                                    className={classes.tooltip}
                                >
                                    {format(
                                        "Contains product units that will be removed from the order if location is deselected"
                                    )}
                                </span>
                            }
                            placement="top"
                            arrow={true}
                        >
                            <ErrorIcon
                                data-test={DATATEST.WORKING_ORDER.DOORS_SELECT.SELECTION_WARNING_ICON}
                                className={classes.warningIcon}
                            />
                        </Tooltip>
                    ) : null;
                default:
                    return _.get(rowData, dataKey, "--");
            }
        },
        [classes, handleToggleSelectDoor]
    );

    const handleIsRowMatchesSearch = useCallback(
        (row: IRow, search: string = "") =>
            _.compact([row?.display_name, row?.line_1]).some((value) =>
                (value || "").toLowerCase().includes(search.toLowerCase())
            ),
        []
    );

    const handleSave = useCallback(() => {
        const selectedDoors = mapSelectedDoors(doors);
        const selectedDoorsMap = _.keyBy(selectedDoors, "location");
        const showWarning = _.some(
            Array.from(shippingLocationsWithQuantitiesSet),
            (location) => !selectedDoorsMap[location]
        );
        showWarning ? handleShowWarningModal() : onSave(selectedDoors);
    }, [doors, onSave, handleShowWarningModal, shippingLocationsWithQuantitiesSet]);

    const handleWarningModalSave = useCallback(() => {
        onSave(mapSelectedDoors(doors));
        handleHideWarningModal();
    }, [doors, onSave, handleHideWarningModal]);

    return (
        <Dialog
            open={open}
            onClose={onCancel}
            classes={{ root: classes.commonDialog, paper: classnames(classes.paper, classes.dialog) }}
        >
            <DialogHeader title={format("Edit Shipping Locations")} onClose={onCancel} />
            <DialogContent>
                <SortableTableWithSearch<IRow>
                    rows={doors}
                    columnsConfig={columnsConfig}
                    onUpdateRowIndex={handleUpdateRowIndex}
                    cellRenderer={cellRenderer}
                    onIsRowMatchesSearch={handleIsRowMatchesSearch}
                    noRowsFoundDataTest={DATATEST.WORKING_ORDER.DOORS_SELECT.NO_RESULTS_MESSAGE}
                    searchDataTest={DATATEST.WORKING_ORDER.DOORS_SELECT.SEARCH}
                />

                <WarningModal open={isWarningOpen} onClose={handleHideWarningModal} onSave={handleWarningModalSave} />
            </DialogContent>
            <DialogFooter onSave={handleSave} onClose={onCancel} saveText={format("Save Locations")} />
        </Dialog>
    );
};

const WarningModal = (props: { open: boolean; onClose: () => void; onSave: () => void }) => {
    const { open, onClose, onSave } = props;
    const classes = useStyles();
    return (
        <Dialog
            data-test={DATATEST.WORKING_ORDER.SELECTION_REMOVE_CONFIRMATION.ROOT}
            open={open}
            onClose={onClose}
            classes={{ root: classes.commonDialog, paper: classes.warningDialog }}
        >
            <DialogHeader
                title={
                    <span
                        data-test={DATATEST.WORKING_ORDER.SELECTION_REMOVE_CONFIRMATION.TITLE}
                        className={classes.warningDialogTitle}
                    >
                        <ErrorOutlineIcon className={classes.warningDialogIcon} />
                        {format("Confirm shipping location removal")}
                    </span>
                }
                onClose={onClose}
            />
            <DialogContent>
                <div data-test={DATATEST.WORKING_ORDER.SELECTION_REMOVE_CONFIRMATION.MESSAGE} className={classes.text}>
                    {format("One or more shipping locations that you have deselected contains product units.")}
                </div>
            </DialogContent>
            <DialogFooter onSave={onSave} onClose={onClose} saveText={format("Confirm")} />
        </Dialog>
    );
};

const getShippingLocationsWithQuantitiesSet = (lineItems: ILineItem[]) => {
    const locationsWithQuantities = _.chain(lineItems)
        .flatMap((lineItem) =>
            _.flatMap(lineItem?.sizes, (size) => (size.location && size.quantity > 0 ? [size.location] : []))
        )
        .uniq()
        .value();
    return new Set(locationsWithQuantities);
};

const getDoorsToDisplay = (
    addresses: IAddress[],
    selectedShippingLocations: ISelectedShippingLocation[],
    shippingLocationsWithQuantitiesSet: Set<string>
) => {
    const selectedShippingLocationsSet = new Set(_.map(selectedShippingLocations, "location"));
    return _.chain(addresses)
        .filter((address) => address.type !== "billing")
        .map((address) => ({
            ...address,
            checked: selectedShippingLocationsSet.has(address._id),
            hasQuantities: shippingLocationsWithQuantitiesSet.has(address._id),
        }))
        .partition(({ _id }) => selectedShippingLocationsSet.has(_id))
        .thru(([selectedList, unselectedList]) => {
            const selectedMap = _.keyBy(selectedList, "_id");
            // ! doors order in selectedShippingLocations matters !
            return [..._.map(selectedShippingLocations, ({ location }) => selectedMap[location]), ...unselectedList];
        })
        .value();
};

const mapSelectedDoors = (doors: IRow[]) =>
    _.chain(doors)
        .filter("checked")
        .map((door) => ({ location: door._id }))
        .value();

const useStyles = makeStyles(() => ({
    ellipsis: {
        overflow: "hidden",
        whiteSpace: "nowrap",
        textOverflow: "ellipsis",
        pointerEvents: "auto",
    },
    tooltip: {
        fontSize: 12,
        lineHeight: "18px",
    },
    dialog: {
        width: "70%",
    },
    commonDialog: {
        zIndex: "1500 !important" as any,
    },
    paper: {
        maxWidth: 850,
    },
    content: {
        position: "relative",
        paddingTop: 20,
        paddingBottom: 20,
    },
    selectedLocations: {
        position: "relative",
    },
    selectedLocationsText: {
        position: "absolute",
        right: 0,
        top: 0,
        textTransform: "none",
    },
    checkbox: {
        backgroundColor: "transparent !important", // force disable ripple effect
        color: theme.palette.grey,
        "& .MuiSvgIcon-root": {
            fontSize: 24,
        },
        "&.Mui-checked": {
            color: theme.palette.primary1Color,
        },
    },
    warningIcon: {
        fontSize: 24,
        color: "#FFD300",
        cursor: "pointer",
        pointerEvents: "auto",
    },
    warningDialog: {
        width: "70%",
        maxWidth: 500,
    },
    text: {
        fontSize: 16,
    },
    warningDialogTitle: {
        display: "flex",
        alignItems: "center",
    },
    warningDialogIcon: {
        marginRight: 20,
        fontSize: 24,
        color: "#EB7B74",
    },
}));
